(() => {
  const initSection = (section) => {
    if (!section || section.dataset.jjShoppableReady === 'true') return;
    section.dataset.jjShoppableReady = 'true';

    const rail = section.querySelector('[data-jj-sv-rail]');
    const cards = Array.from(section.querySelectorAll('[data-jj-sv-card]'));
    const autoplay = section.dataset.autoplay === 'true';
    const prev = section.querySelector('[data-jj-sv-prev]');
    const next = section.querySelector('[data-jj-sv-next]');

    const closeVariantPanels = (except = null) => {
      section.querySelectorAll('[data-jj-sv-variant-panel]').forEach((panel) => {
        if (panel !== except) panel.hidden = true;
      });
    };

    const updateArrows = () => {
      if (!rail || !prev || !next) return;
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth - 2);
      prev.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= maxScroll;
      const hasOverflow = maxScroll > 3;
      const arrows = section.querySelector('[data-jj-sv-arrows]');
      if (arrows) arrows.hidden = !hasOverflow;
    };

    const scrollByCard = (direction) => {
      if (!rail) return;
      const firstCard = rail.querySelector('[data-jj-sv-card], .jj-sv-card--placeholder');
      if (!firstCard) return;
      const gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || 0);
      rail.scrollBy({ left: direction * (firstCard.getBoundingClientRect().width + gap), behavior: 'smooth' });
    };

    prev?.addEventListener('click', () => scrollByCard(-1));
    next?.addEventListener('click', () => scrollByCard(1));
    rail?.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows, { passive: true });

    cards.forEach((card) => {
      const video = card.querySelector('video');
      const playButton = card.querySelector('[data-jj-sv-play]');
      const soundButton = card.querySelector('[data-jj-sv-sound]');
      const atc = card.querySelector('[data-jj-sv-atc]');
      const atcText = card.querySelector('[data-jj-sv-atc-text]');
      const select = card.querySelector('[data-jj-sv-variant-select]');
      const panel = card.querySelector('[data-jj-sv-variant-panel]');
      const idInput = card.querySelector('[data-jj-sv-id]');
      const price = card.querySelector('[data-jj-sv-price]');
      const compare = card.querySelector('[data-jj-sv-compare]');
      const thumb = card.querySelector('[data-jj-sv-thumb]');
      const selectedOption = card.querySelector('[data-jj-sv-selected-option]');
      const hasVariants = atc?.dataset.hasVariants === 'true';

      const syncPlayButton = () => {
        if (!video || !playButton) return;
        const playing = !video.paused && !video.ended;
        playButton.classList.toggle('is-playing', playing);
        playButton.setAttribute('aria-pressed', playing ? 'true' : 'false');
        playButton.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      };

      if (video) {
        video.muted = true;
        video.playsInline = true;
        soundButton?.classList.add('is-muted');

        video.addEventListener('play', syncPlayButton);
        video.addEventListener('pause', syncPlayButton);

        playButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (video.paused) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });

        soundButton?.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const shouldUnmute = video.muted;

          if (shouldUnmute) {
            section.querySelectorAll('video').forEach((otherVideo) => {
              if (otherVideo !== video) otherVideo.muted = true;
            });
            section.querySelectorAll('[data-jj-sv-sound]').forEach((otherButton) => {
              if (otherButton !== soundButton) {
                otherButton.classList.add('is-muted');
                otherButton.setAttribute('aria-pressed', 'false');
                otherButton.setAttribute('aria-label', 'Turn sound on');
              }
            });
          }

          video.muted = !shouldUnmute;
          soundButton.classList.toggle('is-muted', video.muted);
          soundButton.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
          soundButton.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Mute video');

          if (video.paused) video.play().catch(() => {});
        });
      }

      if (hasVariants && atc && panel && select) {
        atc.addEventListener('click', (event) => {
          if (atc.type === 'submit') return;
          event.preventDefault();
          event.stopPropagation();
          const shouldOpen = panel.hidden;
          closeVariantPanels(panel);
          panel.hidden = !shouldOpen;
          if (shouldOpen) setTimeout(() => select.focus({ preventScroll: true }), 20);
        });

        select.addEventListener('change', () => {
          const option = select.options[select.selectedIndex];
          if (!option || !option.value) return;

          if (idInput) idInput.value = option.value;
          if (price && option.dataset.price) price.textContent = option.dataset.price;

          if (compare) {
            if (option.dataset.compare) {
              compare.textContent = option.dataset.compare;
              compare.classList.remove('is-hidden');
            } else {
              compare.textContent = '';
              compare.classList.add('is-hidden');
            }
          }

          if (thumb && option.dataset.image) {
            thumb.src = option.dataset.image;
            thumb.removeAttribute('srcset');
          }

          if (selectedOption) {
            selectedOption.textContent = option.dataset.title || option.textContent.trim();
            selectedOption.hidden = false;
          }

          atc.type = 'submit';
          if (atcText) atcText.textContent = atc.dataset.addText || 'Add to bag';
          panel.hidden = true;
        });
      }
    });

    document.addEventListener('click', (event) => {
      if (!section.contains(event.target)) return;
      if (!event.target.closest('[data-jj-sv-variant-panel]') && !event.target.closest('[data-jj-sv-atc]')) {
        closeVariantPanels();
      }
    });

    if ('IntersectionObserver' in window) {
      const visibility = new Map();
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const syncAutoplay = () => {
        if (!autoplay || reducedMotion) return;
        let bestVideo = null;
        let bestRatio = 0;

        visibility.forEach((ratio, video) => {
          if (ratio >= 0.55 && ratio > bestRatio) {
            bestRatio = ratio;
            bestVideo = video;
          }
        });

        section.querySelectorAll('video').forEach((video) => {
          if (video === bestVideo) {
            if (video.preload === 'none') video.preload = 'metadata';
            video.play().catch(() => {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;
          visibility.set(video, entry.isIntersecting ? entry.intersectionRatio : 0);

          if (!entry.isIntersecting && !video.paused) video.pause();
        });
        syncAutoplay();
      }, { threshold: [0, 0.25, 0.55, 0.85] });

      section.querySelectorAll('video').forEach((video) => {
        visibility.set(video, 0);
        observer.observe(video);
      });
    }

    requestAnimationFrame(updateArrows);
  };

  const initAll = (root = document) => {
    root.querySelectorAll?.('[data-jj-shoppable-section]').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
